import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSlug } from "@/hooks/useSlug";
import { BuildingForm } from "@/modules/buildings/components/BuildingForm";
import { buildingMutations } from "@/queries/buildings/mutations";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { routes } from "@/routes";

export function EditBuildingPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const buildingId = id ?? "";
  const buildingQueries = useBuildingQueries();
  const buildingsQuery = useQuery(buildingQueries.list());
  const buildingQuery = useQuery(buildingQueries.details({ buildingId }));
  const updateBuilding = buildingMutations.useUpdateBuilding();
  const error = buildingsQuery.error ?? buildingQuery.error;

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (buildingsQuery.isFetching || buildingQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  if (!buildingQuery.data) {
    return <ErrorPage status={404} />;
  }

  return (
    <BuildingForm
      isEditMode
      defaultValues={{ name: buildingQuery.data.name }}
      isSubmitting={updateBuilding.isPending}
      existingBuildings={buildingsQuery.data?.items.filter((building) => building.id !== buildingId) ?? []}
      onSubmit={(values) => {
        updateBuilding.mutate(
          { buildingId, data: { name: values.name } },
          { onSuccess: () => void navigate(routes.buildings.list({ slug })) },
        );
      }}
    />
  );
}
